import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  addDoc, 
  deleteDoc, 
  orderBy, 
  limit, 
  Timestamp,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  User, 
  Course, 
  Lesson, 
  StudentEnrollment, 
  VideoProgress, 
  StudentProgress, 
  TeacherDashboardStats, 
  LessonEngagement,
  StudentDetail
} from '../types';

// الحصول على إحصائيات لوحة تحكم المعلم
export const getTeacherDashboardStats = async (teacherId: string): Promise<TeacherDashboardStats> => {
  try {
    // الحصول على الكورسات الخاصة بالمعلم
    const coursesQuery = query(
      collection(db, 'courses'),
      where('teacherId', '==', teacherId)
    );
    const coursesSnapshot = await getDocs(coursesQuery);
    const courses = coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Course));
    
    // جمع معرفات الكورسات
    const courseIds = courses.map(course => course.id);
    
    // الحصول على جميع الاشتراكات في كورسات المعلم
    const enrollmentsQuery = query(
      collection(db, 'enrollments'),
      where('courseId', 'in', courseIds.length > 0 ? courseIds : ['placeholder'])
    );
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    const enrollments = enrollmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StudentEnrollment));
    
    // جمع معرفات الطلاب المشتركين
    const studentIds = [...new Set(enrollments.map(enrollment => enrollment.userId))];
    
    // الحصول على معلومات الطلاب
    const studentsData: StudentProgress[] = [];
    const recentEnrollments: StudentEnrollment[] = [];
    
    // حساب عدد الطلاب لكل كورس
    const studentsPerCourse: { [courseId: string]: number } = {};
    const progressPerCourse: { [courseId: string]: number } = {};
    
    // إعداد البيانات الأولية
    courseIds.forEach(courseId => {
      studentsPerCourse[courseId] = 0;
      progressPerCourse[courseId] = 0;
    });
    
    // حساب عدد الطلاب وتقدمهم لكل كورس
    enrollments.forEach(enrollment => {
      if (studentsPerCourse[enrollment.courseId] !== undefined) {
        studentsPerCourse[enrollment.courseId]++;
        progressPerCourse[enrollment.courseId] += enrollment.progress || 0;
      }
    });
    
    // حساب متوسط التقدم لكل كورس
    Object.keys(progressPerCourse).forEach(courseId => {
      if (studentsPerCourse[courseId] > 0) {
        progressPerCourse[courseId] = progressPerCourse[courseId] / studentsPerCourse[courseId];
      }
    });
    
    // الحصول على أحدث 5 اشتراكات
    const sortedEnrollments = [...enrollments].sort((a, b) => b.enrollmentDate - a.enrollmentDate);
    const top5Enrollments = sortedEnrollments.slice(0, 5);
    
    // إضافة أسماء الطلاب إلى الاشتراكات الحديثة
    for (const enrollment of top5Enrollments) {
      const userDoc = await getDoc(doc(db, 'users', enrollment.userId));
      const userData = userDoc.exists() ? userDoc.data() as User : null;
      
      // إنشاء نسخة جديدة من الاشتراك مع إضافة اسم الطالب
      const enrollmentWithName = {
        ...enrollment,
        studentName: userData?.name || 'طالب'
      } as StudentEnrollment;
      
      recentEnrollments.push(enrollmentWithName);
    }
    
    // الحصول على تقدم الطلاب
    for (const userId of studentIds) {
      // الحصول على معلومات المستخدم
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) continue;
      
      const userData = userDoc.data() as User;
      
      // الحصول على اشتراكات الطالب
      const userEnrollments = enrollments.filter(e => e.userId === userId);
      
      // حساب إجمالي وقت المشاهدة والتقدم
      let totalWatchTime = 0;
      let totalProgress = 0;
      
      for (const enrollment of userEnrollments) {
        // الحصول على تقدم الفيديو للطالب في هذا الكورس
        const videoProgressQuery = query(
          collection(db, 'videoProgress'),
          where('userId', '==', userId),
          where('courseId', '==', enrollment.courseId)
        );
        
        const videoProgressSnapshot = await getDocs(videoProgressQuery);
        const videoProgressData = videoProgressSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoProgress));
        
        // حساب وقت المشاهدة
        videoProgressData.forEach(vp => {
          totalWatchTime += (vp.watchedSeconds || 0);
        });
        
        // حساب التقدم
        totalProgress += enrollment.progress || 0;
      }
      
      // حساب متوسط التقدم
      const averageProgress = userEnrollments.length > 0 ? totalProgress / userEnrollments.length : 0;
      
      // حساب درجة المشاركة (مزيج من التقدم ووقت المشاهدة)
      const engagementScore = (averageProgress * 0.7) + (Math.min(totalWatchTime / 3600, 10) * 0.3);
      
      // إضافة بيانات تقدم الطالب
      for (const enrollment of userEnrollments) {
        // الحصول على معلومات الكورس
        const course = courses.find(c => c.id === enrollment.courseId);
        if (!course) continue;
        
        // الحصول على عدد الدروس في الكورس
        const lessonsQuery = query(
          collection(db, 'lessons'),
          where('courseId', '==', enrollment.courseId)
        );
        const lessonsSnapshot = await getDocs(lessonsQuery);
        const totalLessons = lessonsSnapshot.size;
        
        // حساب عدد الدروس المكتملة
        const completedLessons = enrollment.completedLessons?.length || 0;
        
        studentsData.push({
          userId,
          userName: userData.name,
          userEmail: userData.email,
          userPhoto: userData.photoURL,
          courseId: enrollment.courseId,
          courseName: course.title,
          enrollmentDate: enrollment.enrollmentDate,
          lastAccessDate: enrollment.lastAccessDate,
          progress: enrollment.progress || 0,
          completedLessons,
          totalLessons,
          watchTime: totalWatchTime,
          engagementScore,
          createdAt: enrollment.createdAt,
          updatedAt: enrollment.updatedAt
        });
      }
    }
    
    // ترتيب الطلاب حسب درجة المشاركة
    const sortedStudents = [...studentsData].sort((a, b) => (b.engagementScore || 0) - (a.engagementScore || 0));
    
    // الحصول على أفضل 5 طلاب
    const topPerformingStudents = sortedStudents.slice(0, 5);
    
    // الحصول على أقل 5 طلاب مشاركة
    const lowEngagementStudents = [...sortedStudents].sort((a, b) => (a.engagementScore || 0) - (b.engagementScore || 0)).slice(0, 5);
    
    // حساب إجمالي الإيرادات
    const totalRevenue = courses.reduce((sum, course) => sum + (course.price * (course.enrolledCount || 0)), 0);
    
    // حساب متوسط التقدم والمشاركة
    const totalStudents = studentIds.length;
    const activeStudents = enrollments.filter(e => e.lastAccessDate && (Date.now() - e.lastAccessDate) < 30 * 24 * 60 * 60 * 1000).length;
    const totalCourses = courses.length;
    
    // حساب إجمالي الدروس
    let totalLessons = 0;
    for (const courseId of courseIds) {
      const lessonsQuery = query(
        collection(db, 'lessons'),
        where('courseId', '==', courseId)
      );
      const lessonsSnapshot = await getDocs(lessonsQuery);
      totalLessons += lessonsSnapshot.size;
    }
    
    // حساب متوسط التقدم والمشاركة
    const averageProgress = studentsData.length > 0 
      ? studentsData.reduce((sum, student) => sum + student.progress, 0) / studentsData.length 
      : 0;
    
    const averageEngagement = studentsData.length > 0 
      ? studentsData.reduce((sum, student) => sum + (student.engagementScore || 0), 0) / studentsData.length 
      : 0;
    
    // حساب إجمالي وقت المشاهدة
    const totalWatchTime = studentsData.reduce((sum, student) => sum + student.watchTime, 0);
    
    return {
      totalStudents,
      activeStudents,
      totalCourses,
      totalLessons,
      totalRevenue,
      totalWatchTime,
      averageProgress,
      averageEngagement,
      studentsPerCourse,
      progressPerCourse,
      recentEnrollments,
      topPerformingStudents,
      lowEngagementStudents
    };
    
  } catch (error) {
    console.error('Error getting teacher dashboard stats:', error);
    throw error;
  }
};

// الحصول على تفاصيل طالب معين
export const getStudentDetails = async (studentId: string, teacherId: string): Promise<StudentDetail | null> => {
  try {
    // الحصول على معلومات الطالب
    const userDoc = await getDoc(doc(db, 'users', studentId));
    if (!userDoc.exists()) {
      return null;
    }
    
    const userData = userDoc.data() as User;
    
    // الحصول على كورسات المعلم
    const coursesQuery = query(
      collection(db, 'courses'),
      where('teacherId', '==', teacherId)
    );
    const coursesSnapshot = await getDocs(coursesQuery);
    const courseIds = coursesSnapshot.docs.map(doc => doc.id);
    
    // الحصول على اشتراكات الطالب في كورسات المعلم
    const enrollmentsQuery = query(
      collection(db, 'enrollments'),
      where('userId', '==', studentId),
      where('courseId', 'in', courseIds.length > 0 ? courseIds : ['placeholder'])
    );
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    const enrollments = enrollmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StudentEnrollment));
    
    // الحصول على تقدم الفيديو للطالب
    const videoProgressQuery = query(
      collection(db, 'videoProgress'),
      where('userId', '==', studentId),
      where('courseId', 'in', courseIds.length > 0 ? courseIds : ['placeholder'])
    );
    const videoProgressSnapshot = await getDocs(videoProgressQuery);
    const videoProgressList = videoProgressSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VideoProgress));
    
    // تنظيم تقدم الفيديو حسب معرف الدرس
    const videoProgress: { [lessonId: string]: VideoProgress } = {};
    videoProgressList.forEach(vp => {
      videoProgress[vp.lessonId] = vp;
    });
    
    // حساب تقدم الطالب لكل كورس
    const progress: { [courseId: string]: StudentProgress } = {};
    let totalWatchTime = 0;
    let totalProgress = 0;
    
    for (const enrollment of enrollments) {
      // الحصول على معلومات الكورس
      const courseDoc = await getDoc(doc(db, 'courses', enrollment.courseId));
      if (!courseDoc.exists()) continue;
      
      const courseData = courseDoc.data() as Course;
      
      // الحصول على دروس الكورس
      const lessonsQuery = query(
        collection(db, 'lessons'),
        where('courseId', '==', enrollment.courseId)
      );
      const lessonsSnapshot = await getDocs(lessonsQuery);
      const totalLessons = lessonsSnapshot.size;
      
      // حساب عدد الدروس المكتملة
      const completedLessons = enrollment.completedLessons?.length || 0;
      
      // حساب وقت المشاهدة للكورس
      const courseVideoProgress = videoProgressList.filter(vp => vp.courseId === enrollment.courseId);
      const courseWatchTime = courseVideoProgress.reduce((sum, vp) => sum + (vp.watchedSeconds || 0), 0);
      
      totalWatchTime += courseWatchTime;
      totalProgress += enrollment.progress || 0;
      
      // إنشاء كائن تقدم الطالب للكورس
      progress[enrollment.courseId] = {
        userId: studentId,
        userName: userData.name,
        userEmail: userData.email,
        userPhoto: userData.photoURL,
        courseId: enrollment.courseId,
        courseName: courseData.title,
        enrollmentDate: enrollment.enrollmentDate,
        lastAccessDate: enrollment.lastAccessDate,
        progress: enrollment.progress || 0,
        completedLessons,
        totalLessons,
        watchTime: courseWatchTime,
        createdAt: enrollment.createdAt,
        updatedAt: enrollment.updatedAt
      };
    }
    
    // حساب متوسط التقدم
    const averageProgress = enrollments.length > 0 ? totalProgress / enrollments.length : 0;
    
    // حساب آخر نشاط
    const lastActivity = Math.max(
      ...enrollments.map(e => e.lastAccessDate || 0),
      ...videoProgressList.map(vp => vp.lastWatched || 0)
    );
    
    // حساب درجة المشاركة
    const engagementScore = (averageProgress * 0.7) + (Math.min(totalWatchTime / 3600, 10) * 0.3);
    
    return {
      user: userData,
      enrollments,
      progress,
      videoProgress,
      totalWatchTime,
      averageProgress,
      lastActivity,
      engagementScore
    };
    
  } catch (error) {
    console.error('Error getting student details:', error);
    throw error;
  }
};

// الحصول على تفاصيل مشاركة الطلاب في الدروس
export const getLessonEngagement = async (courseId: string): Promise<LessonEngagement[]> => {
  try {
    // الحصول على دروس الكورس
    const lessonsQuery = query(
      collection(db, 'lessons'),
      where('courseId', '==', courseId)
    );
    const lessonsSnapshot = await getDocs(lessonsQuery);
    const lessons = lessonsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lesson));
    
    const result: LessonEngagement[] = [];
    
    for (const lesson of lessons) {
      // الحصول على تقدم الفيديو لهذا الدرس
      const videoProgressQuery = query(
        collection(db, 'videoProgress'),
        where('lessonId', '==', lesson.id)
      );
      const videoProgressSnapshot = await getDocs(videoProgressQuery);
      const videoProgressList = videoProgressSnapshot.docs.map(doc => doc.data() as VideoProgress);
      
      // حساب عدد المشاهدات
      const watchCount = videoProgressList.length;
      
      // حساب متوسط وقت المشاهدة
      const totalWatchTime = videoProgressList.reduce((sum, vp) => sum + (vp.watchedSeconds || 0), 0);
      const averageWatchTime = watchCount > 0 ? totalWatchTime / watchCount : 0;
      
      // حساب معدل الإكمال
      const completedCount = videoProgressList.filter(vp => vp.isCompleted).length;
      const completionRate = watchCount > 0 ? completedCount / watchCount : 0;
      
      // حساب متوسط التقدم
      const totalProgress = videoProgressList.reduce((sum, vp) => sum + vp.progress, 0);
      const averageProgress = watchCount > 0 ? totalProgress / watchCount : 0;
      
      result.push({
        lessonId: lesson.id,
        lessonTitle: lesson.title,
        courseId,
        watchCount,
        averageWatchTime,
        completionRate,
        averageProgress
      });
    }
    
    return result;
    
  } catch (error) {
    console.error('Error getting lesson engagement:', error);
    throw error;
  }
};

// إضافة ملاحظة للطالب
export const addStudentNote = async (studentId: string, teacherId: string, note: string): Promise<void> => {
  try {
    const noteRef = doc(db, 'studentNotes', `${teacherId}_${studentId}`);
    
    await setDoc(noteRef, {
      studentId,
      teacherId,
      note,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error adding student note:', error);
    throw error;
  }
};

// الحصول على ملاحظة الطالب
export const getStudentNote = async (studentId: string, teacherId: string): Promise<{ note: string; updatedAt?: number } | null> => {
  try {
    const noteRef = doc(db, 'studentNotes', `${teacherId}_${studentId}`);
    const noteDoc = await getDoc(noteRef);
    
    if (noteDoc.exists()) {
      const data = noteDoc.data();
      return { 
        note: data.note || '',
        updatedAt: data.updatedAt?.toMillis() || Date.now()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting student note:', error);
    throw error;
  }
};

// الحصول على جميع طلاب المعلم
export const getTeacherStudents = async (teacherId: string): Promise<User[]> => {
  try {
    // الحصول على كورسات المعلم
    const coursesQuery = query(
      collection(db, 'courses'),
      where('teacherId', '==', teacherId)
    );
    const coursesSnapshot = await getDocs(coursesQuery);
    const courseIds = coursesSnapshot.docs.map(doc => doc.id);
    
    if (courseIds.length === 0) {
      return [];
    }
    
    // الحصول على اشتراكات الطلاب في كورسات المعلم
    const enrollmentsQuery = query(
      collection(db, 'enrollments'),
      where('courseId', 'in', courseIds)
    );
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    const studentIds = [...new Set(enrollmentsSnapshot.docs.map(doc => doc.data().userId))];
    
    // الحصول على معلومات الطلاب
    const students: User[] = [];
    
    for (const studentId of studentIds) {
      const userDoc = await getDoc(doc(db, 'users', studentId));
      if (userDoc.exists()) {
        students.push({ id: userDoc.id, ...userDoc.data() } as User);
      }
    }
    
    return students;
    
  } catch (error) {
    console.error('Error getting teacher students:', error);
    throw error;
  }
};

// الحصول على كورسات المعلم
export const getCoursesByTeacherId = async (teacherId: string): Promise<Course[]> => {
  try {
    const coursesQuery = query(
      collection(db, 'courses'),
      where('teacherId', '==', teacherId)
    );
    const coursesSnapshot = await getDocs(coursesQuery);
    return coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Course));
  } catch (error) {
    console.error('Error getting teacher courses:', error);
    return [];
  }
};

// الحصول على أكواد الكورس
export const getCourseCodes = async (courseId: string): Promise<Array<{ id: string; code: string; expiryDate: Date; used: boolean }>> => {
  try {
    const codesQuery = query(
      collection(db, 'courseCodes'),
      where('courseId', '==', courseId)
    );
    const codesSnapshot = await getDocs(codesQuery);
    return codesSnapshot.docs.map(doc => ({
      id: doc.id,
      code: doc.data().code,
      used: doc.data().used,
      expiryDate: (doc.data().expiryDate as Timestamp).toDate()
    }));
  } catch (error) {
    console.error('Error getting course codes:', error);
    return [];
  }
};

// إنشاء كود جديد للكورس
export const generateCourseCode = async (courseId: string, expiryDate: Date): Promise<string> => {
  try {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    await addDoc(collection(db, 'courseCodes'), {
      courseId,
      code,
      expiryDate: Timestamp.fromDate(expiryDate),
      used: false,
      createdAt: serverTimestamp()
    });
    return code;
  } catch (error) {
    console.error('Error generating course code:', error);
    throw error;
  }
};

// تحديث تاريخ انتهاء صلاحية الكود
export const updateCodeExpiryDate = async (codeId: string, newExpiryDate: Date): Promise<void> => {
  try {
    const codeRef = doc(db, 'courseCodes', codeId);
    await updateDoc(codeRef, {
      expiryDate: Timestamp.fromDate(newExpiryDate)
    });
  } catch (error) {
    console.error('Error updating code expiry date:', error);
    throw error;
  }
};

// تحديث تقدم الطالب في الدرس
export const updateStudentLessonProgress = async (
  studentId: string,
  courseId: string,
  lessonId: string,
  progress: number,
  completed: boolean
): Promise<void> => {
  try {
    // التحقق من وجود تسجيل للتقدم
    const progressRef = doc(db, 'videoProgress', `${studentId}_${lessonId}`);
    
    const now = serverTimestamp();
    
    if ((await getDoc(progressRef)).exists()) {
      // تحديث التقدم الحالي
      await updateDoc(progressRef, {
        progress,
        completed,
        lastWatched: now,
        updatedAt: now
      });
    } else {
      // إنشاء تقدم جديد
      await setDoc(progressRef, {
        userId: studentId,
        courseId,
        lessonId,
        progress,
        completed,
        watchedSeconds: 0,
        lastWatched: now,
        createdAt: now,
        updatedAt: now
      });
    }
    
    // تحديث تقدم الطالب في الكورس
    await updateStudentCourseProgress(studentId, courseId);
    
  } catch (error) {
    console.error('Error updating student lesson progress:', error);
    throw error;
  }
};

// تحديث تقدم الطالب في الكورس
export const updateStudentCourseProgress = async (
  studentId: string,
  courseId: string
): Promise<void> => {
  try {
    // الحصول على جميع دروس الكورس
    const lessonsQuery = query(
      collection(db, 'lessons'),
      where('courseId', '==', courseId)
    );
    const lessonsSnapshot = await getDocs(lessonsQuery);
    const totalLessons = lessonsSnapshot.size;
    
    if (totalLessons === 0) return;
    
    // الحصول على تقدم الطالب في جميع دروس الكورس
    const progressQuery = query(
      collection(db, 'videoProgress'),
      where('userId', '==', studentId),
      where('courseId', '==', courseId)
    );
    const progressSnapshot = await getDocs(progressQuery);
    const lessonProgress = progressSnapshot.docs.map(doc => doc.data());
    
    // حساب عدد الدروس المكتملة
    const completedLessons = lessonProgress.filter(lp => lp.completed).length;
    
    // حساب نسبة التقدم الإجمالية
    const overallProgress = totalLessons > 0 ? (completedLessons / totalLessons) : 0;
    
    // تحديث تسجيل الطالب في الكورس
    const enrollmentQuery = query(
      collection(db, 'enrollments'),
      where('userId', '==', studentId),
      where('courseId', '==', courseId)
    );
    const enrollmentSnapshot = await getDocs(enrollmentQuery);
    
    if (!enrollmentSnapshot.empty) {
      const enrollmentDoc = enrollmentSnapshot.docs[0];
      const completedLessonIds = lessonProgress
        .filter(lp => lp.completed)
        .map(lp => lp.lessonId);
      
      await updateDoc(doc(db, 'enrollments', enrollmentDoc.id), {
        progress: overallProgress,
        completedLessons: completedLessonIds,
        lastAccessDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
  } catch (error) {
    console.error('Error updating student course progress:', error);
    throw error;
  }
};

// الحصول على الدورات المسجل فيها الطالب
export const getStudentCourses = async (studentId: string, teacherId: string): Promise<Array<StudentEnrollment & { completedLessonsCount?: number; totalLessons?: number }>> => {
  try {
    // الحصول على كورسات المعلم
    const coursesQuery = query(
      collection(db, 'courses'),
      where('teacherId', '==', teacherId)
    );
    const coursesSnapshot = await getDocs(coursesQuery);
    const courses = coursesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Course[];
    const courseIds = courses.map(course => course.id);
    
    if (courseIds.length === 0) {
      return [];
    }
    
    // الحصول على اشتراكات الطالب في كورسات المعلم
    const enrollmentsQuery = query(
      collection(db, 'enrollments'),
      where('userId', '==', studentId),
      where('courseId', 'in', courseIds)
    );
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    
    const enrollments: Array<StudentEnrollment & { completedLessonsCount?: number; totalLessons?: number }> = [];
    
    for (const enrollmentDoc of enrollmentsSnapshot.docs) {
      const enrollment = { id: enrollmentDoc.id, ...enrollmentDoc.data() } as StudentEnrollment;
      
      // الحصول على معلومات الطالب
      const userDoc = await getDoc(doc(db, 'users', enrollment.userId));
      const userData = userDoc.exists() ? userDoc.data() as User : null;
      
      // الحصول على معلومات الكورس
      const course = courses.find(c => c.id === enrollment.courseId);
      
      // الحصول على معلومات تقدم الطالب في الكورس
      const progressQuery = query(
        collection(db, 'progress'),
        where('userId', '==', studentId),
        where('courseId', '==', enrollment.courseId)
      );
      const progressSnapshot = await getDocs(progressQuery);
      
      let completedLessonsCount = 0;
      let totalLessons = 0;
      let progress = enrollment.progress || 0;
      
      if (!progressSnapshot.empty) {
        const progressData = progressSnapshot.docs[0].data();
        completedLessonsCount = progressData.completedLessons?.length || 0;
        totalLessons = progressData.totalLessons || 0;
        
        // إذا كان هناك تقدم في جدول progress، نستخدمه
        if (progressData.progress !== undefined) {
          progress = progressData.progress;
        }
      } else {
        // إذا لم يكن هناك تقدم، نحصل على عدد الدروس في الكورس
        const lessonsQuery = query(
          collection(db, 'lessons'),
          where('courseId', '==', enrollment.courseId)
        );
        const lessonsSnapshot = await getDocs(lessonsQuery);
        totalLessons = lessonsSnapshot.size;
        
        // completedLessons قد تكون موجودة في الاشتراك نفسه
        completedLessonsCount = enrollment.completedLessons?.length || 0;
      }
      
      enrollments.push({
        ...enrollment,
        studentName: userData?.name || 'طالب',
        courseName: course?.title || enrollment.courseName || 'دورة غير معروفة',
        progress: progress,
        completedLessonsCount: completedLessonsCount,
        totalLessons: totalLessons
      } as StudentEnrollment & { completedLessonsCount?: number; totalLessons?: number });
    }
    
    return enrollments;
  } catch (error) {
    console.error('Error getting student courses:', error);
    throw error;
  }
};

// الحصول على إحصائيات الطالب
export const getStudentStats = async (studentId: string, teacherId: string): Promise<{
  totalCourses: number;
  totalLessonsWatched: number;
  totalWatchTime: number;
  averageProgress: number;
  lastActivity?: number;
  completionRate: number;
}> => {
  try {
    console.log(`جاري الحصول على إحصائيات الطالب: ${studentId} للمعلم: ${teacherId}`);
    
    // الحصول على كورسات المعلم
    const coursesQuery = query(
      collection(db, 'courses'),
      where('teacherId', '==', teacherId)
    );
    const coursesSnapshot = await getDocs(coursesQuery);
    const courseIds = coursesSnapshot.docs.map(doc => doc.id);
    
    if (courseIds.length === 0) {
      console.log('لا توجد كورسات للمعلم');
      return {
        totalCourses: 0,
        totalLessonsWatched: 0,
        totalWatchTime: 0,
        averageProgress: 0,
        completionRate: 0
      };
    }
    
    // الحصول على اشتراكات الطالب في كورسات المعلم
    const enrollmentsQuery = query(
      collection(db, 'enrollments'),
      where('userId', '==', studentId),
      where('courseId', 'in', courseIds)
    );
    const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
    
    if (enrollmentsSnapshot.empty) {
      console.log('الطالب غير مسجل في أي كورس للمعلم');
      return {
        totalCourses: 0,
        totalLessonsWatched: 0,
        totalWatchTime: 0,
        averageProgress: 0,
        completionRate: 0
      };
    }
    
    console.log(`تم العثور على ${enrollmentsSnapshot.size} اشتراك للطالب`);
    
    const totalCourses = enrollmentsSnapshot.size;
    let totalProgress = 0;
    let lastActivity = 0;
    let totalLessonsWatched = 0;
    let totalCompletedCourses = 0;
    
    // جمع معلومات من جدول الاشتراكات
    for (const enrollmentDoc of enrollmentsSnapshot.docs) {
      const enrollment = enrollmentDoc.data() as StudentEnrollment;
      
      // إضافة التقدم من الاشتراك
      totalProgress += enrollment.progress || 0;
      
      // إضافة الدروس المكتملة من الاشتراك
      if (enrollment.completedLessons) {
        totalLessonsWatched += enrollment.completedLessons.length;
      }
      
      // تحديث آخر نشاط
      if (enrollment.lastAccessDate && enrollment.lastAccessDate > lastActivity) {
        lastActivity = enrollment.lastAccessDate;
      }
      
      // إضافة الكورسات المكتملة
      if ((enrollment.progress || 0) >= 100) {
        totalCompletedCourses++;
      }
    }
    
    console.log(`إجمالي التقدم: ${totalProgress}, إجمالي الدروس المشاهدة: ${totalLessonsWatched}`);
    
    // الحصول على تقدم الطالب في جميع الدروس (جدول progress)
    const progressQuery = query(
      collection(db, 'progress'),
      where('userId', '==', studentId)
    );
    const progressSnapshot = await getDocs(progressQuery);
    
    console.log(`تم العثور على ${progressSnapshot.size} سجل تقدم للطالب`);
    
    // تصفية سجلات التقدم للكورسات التي يدرسها المعلم فقط
    const filteredProgressDocs = progressSnapshot.docs.filter(doc => {
      const data = doc.data();
      return courseIds.includes(data.courseId);
    });
    
    for (const progressDoc of filteredProgressDocs) {
      const progressData = progressDoc.data();
      
      // تحديث آخر نشاط
      if (progressData.updatedAt && progressData.updatedAt > lastActivity) {
        lastActivity = progressData.updatedAt;
      }
      
      // إضافة معلومات الإكمال إذا لم تكن موجودة في الاشتراك
      if (progressData.isCompleted) {
        totalLessonsWatched++;
      }
    }
    
    // الحصول على وقت المشاهدة من جدول videoProgress
    let totalWatchTime = 0;
    const videoProgressQuery = query(
      collection(db, 'videoProgress'),
      where('userId', '==', studentId)
    );
    const videoProgressSnapshot = await getDocs(videoProgressQuery);
    
    console.log(`تم العثور على ${videoProgressSnapshot.size} سجل تقدم فيديو للطالب`);
    
    // تصفية سجلات تقدم الفيديو للكورسات التي يدرسها المعلم فقط
    const filteredVideoProgressDocs = videoProgressSnapshot.docs.filter(doc => {
      const data = doc.data();
      return courseIds.includes(data.courseId);
    });
    
    for (const videoProgressDoc of filteredVideoProgressDocs) {
      const videoProgressData = videoProgressDoc.data() as VideoProgress;
      
      // إضافة وقت المشاهدة
      totalWatchTime += videoProgressData.watchedSeconds || 0;
      
      // تحديث آخر نشاط
      if (videoProgressData.lastWatched && videoProgressData.lastWatched > lastActivity) {
        lastActivity = videoProgressData.lastWatched;
      }
    }
    
    console.log(`إجمالي وقت المشاهدة: ${totalWatchTime} ثانية`);
    
    // إذا لم يكن هناك تقدم في الفيديو، نحاول الحصول على وقت المشاهدة من جدول progress
    if (totalWatchTime === 0) {
      for (const progressDoc of filteredProgressDocs) {
        const progressData = progressDoc.data();
        totalWatchTime += progressData.watchedDuration || 0;
      }
      console.log(`إجمالي وقت المشاهدة بعد إضافة جدول progress: ${totalWatchTime} ثانية`);
    }
    
    const averageProgress = totalCourses > 0 ? totalProgress / totalCourses : 0;
    const completionRate = totalCourses > 0 ? (totalCompletedCourses / totalCourses) * 100 : 0;
    
    const result = {
      totalCourses,
      totalLessonsWatched,
      totalWatchTime,
      averageProgress,
      lastActivity: lastActivity > 0 ? lastActivity : undefined,
      completionRate
    };
    
    console.log('إحصائيات الطالب:', result);
    return result;
  } catch (error) {
    console.error('Error getting student stats:', error);
    throw error;
  }
};
